import type { Cohort, ConfigResponse } from '../types';

/**
 * Set Type: two values, one of them derived.
 *
 * Either every selected cohort is pushed as one combined audience, or each gets
 * an audience of its own — in which case the number of sets *is* the number of
 * selected cohorts. That makes the second value a moving target: tick or untick
 * a cohort and it has to follow, or the document goes out claiming a set count
 * that contradicts the cohorts listed directly above it.
 *
 * The wording itself comes from the server (`/config`), so the two values are
 * defined in one place rather than drifting between the parser, the document and
 * this form.
 */

export function countSelected(cohorts: Cohort[]): number {
  return cohorts.filter((cohort) => cohort.selected).length;
}

/** `4 (Individual Audience for each cohort)` for four selected cohorts. */
export function individualSetType(config: ConfigResponse, selected: number): string {
  return `${Math.max(1, selected)} (${config.setType.individualSuffix})`;
}

/** The two suggestions offered beside the field, in a stable order. */
export function setTypeOptions(config: ConfigResponse, selected: number): string[] {
  return [config.setType.combined, individualSetType(config, selected)];
}

/**
 * True when a value is the per-cohort form, whatever count it currently names.
 *
 * Matched on the wording rather than on an exact string, so a value that has
 * fallen out of step with the cohort count — the very thing this module exists
 * to fix — is still recognised as the per-cohort form.
 */
export function isIndividualSetType(value: string): boolean {
  return /individual|each cohort|per cohort/i.test(value);
}

/**
 * Keeps the per-cohort count in step with the cohorts actually selected.
 *
 * Only ever rewrites a value that is already the per-cohort form. A combined
 * value, a blank, or wording the operator typed themselves is returned exactly
 * as it was — an edit made by hand must not be undone by ticking a checkbox.
 */
export function syncSetType(value: string, config: ConfigResponse, selected: number): string {
  if (!isIndividualSetType(value)) return value;

  const next = individualSetType(config, selected);
  return next === value ? value : next;
}
